import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { Toaster } from 'sonner'
import MaterialLayout from './components/MaterialLayout'
import HomePage from './pages/HomePage'
import AssessmentPage from './pages/AssessmentPage'
import ResultsPage from './pages/ResultsPage'
import HistoryPage from './pages/HistoryPage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import NotFoundPage from './pages/NotFoundPage'
import LoginForm from './components/LoginForm'
import DataManagement from './components/DataManagement'
import { AuthProvider, useAuth } from './contexts/AuthContext'


function App() {
  // Protected Route Component - moved inside App to have access to AuthProvider
  const ProtectedRoute: React.FC<{ children: React.ReactNode; requireAdmin?: boolean }> = ({ 
    children, 
    requireAdmin = false 
  }) => {
    const { isAuthenticated, isAdmin, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (requireAdmin && !isAdmin) {
      return <Navigate to="/assessment" replace />;
    }

    return <>{children}</>;
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <Helmet>
            <title>Boobalyz - Breast Cancer Risk Prediction</title>
            <meta name="description" content="Advanced breast cancer risk prediction and treatment recommendation system for healthcare professionals." />
            <meta name="keywords" content="breast cancer, risk prediction, healthcare, medical, oncology, treatment recommendations" />
            <meta name="author" content="Boobalyz Team" />
            
            {/* Open Graph */}
            <meta property="og:title" content="Boobalyz - Breast Cancer Risk Prediction" />
            <meta property="og:description" content="Advanced breast cancer risk prediction and treatment recommendation system for healthcare professionals." />
            <link rel="canonical" href="https://boobalyz.com" />
          </Helmet>
          
          <MaterialLayout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/assessment" element={
                <ProtectedRoute>
                  <AssessmentPage />
                </ProtectedRoute>
              } />
              <Route path="/results/:id" element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/data" element={
                <ProtectedRoute requireAdmin>
                  <DataManagement />
                </ProtectedRoute>
              } />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </MaterialLayout>
        </Router>
      </AuthProvider>
      <Toaster richColors position="top-right" />
    </HelmetProvider>
  )
}

export default App